import React, { useMemo, useState } from 'react';
import { Activity, ExternalLink, AlertTriangle, Database, Radio, Server, Globe2, Bug, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-master/components/ui/card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { useErrorTrackingIssuesFeed } from '@/hooks/useClusterRealtime';

interface DashboardEntry {
  key: string;
  title: string;
  description: string;
  icon: React.ElementType;
  path: string;
}

interface ViteEnvMeta {
  env?: {
    VITE_GRAFANA_EMBED_URL?: string;
    VITE_ERROR_TRACKING_URL?: string;
    VITE_ERROR_TRACKING_ORG_SLUG?: string;
    VITE_ERROR_TRACKING_DSN?: string;
  };
}

const DASHBOARDS = [
  {
    key: 'cluster',
    title: 'Cluster Overview',
    description: 'CPU, RAM, disco, network sui 12 nodi cluster (FE-1, FS-1/2, BE-1..4, DB-1/2, REDIS-1, KAM-1, OBS-1).',
    icon: Server,
    path: '/d/edg-cluster/cluster-overview?kiosk=1&refresh=30s',
  },
  {
    key: 'postgres',
    title: 'PostgreSQL HA',
    description: 'Patroni cluster, replica lag, top queries, connessioni, WAL, pg_stat_statements.',
    icon: Database,
    path: '/d/edg-postgres/postgres-ha?kiosk=1&refresh=30s',
  },
  {
    key: 'voice',
    title: 'Voice & SBC',
    description: 'FreeSWITCH FS-1/2 + Kamailio KAM-1: call volume, registrations, INVITE rate, PDD, MOS.',
    icon: Radio,
    path: '/d/edg-voice/voice-sbc?kiosk=1&refresh=30s',
  },
  {
    key: 'synthetic',
    title: 'Synthetic Probes',
    description: 'Blackbox HTTPS sui 6 hostname pubblici + TCP probe SIP 5060/5061 + scadenza certificati TLS.',
    icon: Globe2,
    path: '/d/edg-synthetic/synthetic-probes?kiosk=1&refresh=30s',
  },
] as const satisfies ReadonlyArray<DashboardEntry>;

const DEFAULT_DASHBOARD: DashboardEntry = DASHBOARDS[0];

type ViewKey = 'grafana' | 'error-tracking';

function getViteEnv(): NonNullable<ViteEnvMeta['env']> {
  const meta = import.meta as unknown as ViteEnvMeta;
  return meta.env ?? {};
}

function getGrafanaBase(env: NonNullable<ViteEnvMeta['env']>): string {
  return env.VITE_GRAFANA_EMBED_URL ?? 'https://grafana.easyflowai.it';
}

function getErrorTrackingBase(env: NonNullable<ViteEnvMeta['env']>): string {
  return env.VITE_ERROR_TRACKING_URL ?? 'https://glitchtip.easyflowai.it';
}

const LEVEL_BADGE: Record<string, string> = {
  fatal: 'bg-red-600 text-white',
  error: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
};

function GrafanaView({ grafanaBase }: { grafanaBase: string }) {
  const [active, setActive] = useState<string>(DEFAULT_DASHBOARD.key);
  const current: DashboardEntry = DASHBOARDS.find((d) => d.key === active) ?? DEFAULT_DASHBOARD;
  const CurrentIcon = current.icon;
  const grafanaSrc = `${grafanaBase}${current.path}`;
  const alertsUrl = `${grafanaBase}/alerting/list`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button variant="outline" size="sm" asChild>
          <a href={alertsUrl} target="_blank" rel="noopener noreferrer">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Alerts Grafana
            <ExternalLink className="h-3.5 w-3.5 ml-2" />
          </a>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {DASHBOARDS.map((d) => {
          const Icon = d.icon;
          const isActive = d.key === active;
          return (
            <button
              key={d.key}
              type="button"
              onClick={() => setActive(d.key)}
              className={[
                'text-left p-3 rounded-lg border transition-all',
                isActive
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-500/10 dark:border-purple-400'
                  : 'border-border bg-white dark:bg-slate-900/50 hover:border-purple-300 dark:hover:border-purple-500/50',
              ].join(' ')}
              aria-pressed={isActive}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon
                  className={
                    isActive
                      ? 'h-4 w-4 text-purple-600 dark:text-purple-300'
                      : 'h-4 w-4 text-muted-foreground'
                  }
                />
                <span className="text-sm font-semibold">{d.title}</span>
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug">{d.description}</p>
            </button>
          );
        })}
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="py-3 px-4 flex flex-row items-center gap-2 border-b">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <CurrentIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            {current.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <iframe
            key={grafanaSrc}
            title={`Grafana — ${current.title}`}
            src={grafanaSrc}
            className="w-full border-0"
            style={{ height: 'calc(100vh - 360px)', minHeight: 640 }}
            allow="fullscreen"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
            referrerPolicy="no-referrer"
          />
          <p className="text-[11px] text-muted-foreground px-4 py-2 border-t">
            Se l'iframe è vuoto: verifica DNS <code>grafana.easyflowai.it</code>, TLS Let's Encrypt,
            <code> allow_embedding=true</code> in <code>grafana.ini</code>, e auth anonymous Viewer attiva.
            Vedi runbook <code>observability-cluster.md</code>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function ErrorTrackingView({
  errorTrackingBase,
  dsnConfigured,
}: {
  errorTrackingBase: string;
  dsnConfigured: boolean;
}) {
  const { issues, socketConnected } = useErrorTrackingIssuesFeed();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span
            className={[
              'inline-block h-2 w-2 rounded-full',
              socketConnected ? 'bg-green-500' : 'bg-slate-400',
            ].join(' ')}
            aria-hidden
          />
          {socketConnected ? 'Feed live connesso' : 'Feed live disconnesso'}
        </div>
        <Button variant="outline" size="sm" asChild>
          <a href={`${errorTrackingBase}/`} target="_blank" rel="noopener noreferrer">
            <Bug className="h-4 w-4 mr-2" />
            Apri GlitchTip
            <ExternalLink className="h-3.5 w-3.5 ml-2" />
          </a>
        </Button>
      </div>

      {!dsnConfigured && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/40 p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-800 dark:text-amber-200 leading-snug">
            <strong>Error tracking non ancora provisionato.</strong> Il codice client è attivo ma il
            DSN GlitchTip non è configurato. Deploy istanza self-hosted su OBS-1 + DNS/TLS + org/progetti/DSN
            tracciati nel follow-up #2623. Vedi runbook <code>glitchtip-self-hosted.md</code>.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 overflow-hidden">
          <CardHeader className="py-3 px-4 flex flex-row items-center gap-2 border-b">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Bug className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              GlitchTip
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <iframe
              key={errorTrackingBase}
              title="GlitchTip — Error Tracking"
              src={`${errorTrackingBase}/`}
              className="w-full border-0"
              style={{ height: 'calc(100vh - 400px)', minHeight: 560 }}
              allow="fullscreen"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
              referrerPolicy="no-referrer"
            />
            <p className="text-[11px] text-muted-foreground px-4 py-2 border-t">
              Se l'iframe è vuoto: GlitchTip può impostare <code>X-Frame-Options</code>/CSP che bloccano
              l'embedding — usa il pulsante "Apri GlitchTip". Vedi runbook <code>glitchtip-self-hosted.md</code>.
            </p>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-sm font-semibold">Issue live</CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[560px] overflow-auto">
            {issues.length === 0 ? (
              <p className="text-xs text-muted-foreground p-4">
                Nessuna issue ricevuta in questa sessione. Le nuove issue compaiono qui in tempo reale
                quando il webhook GlitchTip notifica il backend.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {issues.map((issue, idx) => (
                  <li key={`${issue.issueId}-${idx}`} className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={[
                          'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded',
                          LEVEL_BADGE[issue.level] ?? LEVEL_BADGE.error,
                        ].join(' ')}
                      >
                        {issue.level}
                      </span>
                      <span className="text-[10px] text-muted-foreground ml-auto">
                        {new Date(issue.receivedAt).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-xs font-medium break-words">
                      {issue.url ? (
                        <a
                          href={issue.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-600 dark:text-purple-300 hover:underline"
                        >
                          {issue.title}
                        </a>
                      ) : (
                        issue.title
                      )}
                    </p>
                    {issue.project && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">{issue.project}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function ObservabilityDashboards() {
  const env = useMemo(() => getViteEnv(), []);
  const grafanaBase = useMemo(() => getGrafanaBase(env), [env]);
  const errorTrackingBase = useMemo(() => getErrorTrackingBase(env), [env]);
  const dsnConfigured = Boolean((env.VITE_ERROR_TRACKING_DSN ?? '').trim());
  const [view, setView] = useState<ViewKey>('grafana');

  const tabs: ReadonlyArray<{ key: ViewKey; label: string; icon: React.ElementType }> = [
    { key: 'grafana', label: 'Dashboard', icon: BarChart3 },
    { key: 'error-tracking', label: 'Error Tracking', icon: Bug },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-white flex items-center gap-2">
            <Activity className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            Observability
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Metriche cluster da Prometheus/Grafana (<code className="text-xs">{grafanaBase}</code>) +
            error tracking applicativo (<code className="text-xs">{errorTrackingBase}</code>).
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-border">
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = t.key === view;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setView(t.key)}
              className={[
                'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                isActive
                  ? 'border-purple-500 text-purple-600 dark:text-purple-300'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              ].join(' ')}
              aria-pressed={isActive}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {view === 'grafana' ? (
        <GrafanaView grafanaBase={grafanaBase} />
      ) : (
        <ErrorTrackingView errorTrackingBase={errorTrackingBase} dsnConfigured={dsnConfigured} />
      )}
    </div>
  );
}
